import { queryOptions } from '@tanstack/react-query';
import { HydraUIAPIError } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import {
  SEGMENT_OUT_LEGACY_ID,
  parseSegmentOutLegacy,
  segmentOutLegacyKeys,
  type SegmentOutLegacyIntegration,
} from './entity';

/**
 * Legacy Segment source reads.
 *
 * `staleTime: 0` for the same reason as its `segment_out` sibling: the record is
 * mutated by the page that reads it, so a revisit must never serve a stale copy.
 *
 * This is a **different slot** from the one the integrations directory reads. That
 * one lives at `statusKey(workspaceId, 'segdotcom')` in `services/db/integration`
 * and swallows every error into `null`, which suits a connected/not badge but not
 * a form: this page has to tell "never connected" from "we could not load it",
 * because Ember offered a retry for the second and only the second.
 */
const STALE_TIME = 0;

/**
 * @public The legacy record, or `null` when this workspace has none.
 *
 * A 404 is the "no legacy record" answer rather than a failure — the endpoint has
 * no record until one exists. Every other status is a real error and propagates,
 * so the page can offer a retry instead of rendering an empty form over a 5xx.
 */
export async function getSegmentOutLegacy(workspaceId: number): Promise<SegmentOutLegacyIntegration | null> {
  try {
    const data = await hydraUiApi.integrations.getSegdotcom({
      params: { path: { environment_id: String(workspaceId) } },
    });
    return parseSegmentOutLegacy(data);
  } catch (error) {
    if (error instanceof HydraUIAPIError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

/** @public */
export const getSegmentOutLegacyQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: segmentOutLegacyKeys.detail(workspaceId, SEGMENT_OUT_LEGACY_ID),
    queryFn: () => getSegmentOutLegacy(workspaceId),
    staleTime: STALE_TIME,
  });
